import styled, { css } from "styled-components";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";
import {
  BORDER_RADIUS,
  COMPONENTS,
  SPACING,
} from "../../../constants/design/spacing";
import { Row } from "../../shared/Table/style";
import { frostedGlassBg } from "../../shared/style";

export const TradeOrderBookWrapper = styled.div`
  ${FONT_CSS.data.three}
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  justify-content: flex-start;
`;

export const CellsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const MiddleRow = styled(Row)`
  ${FONT_CSS.data.three}
  justify-content: center;
  align-items: center;
  height: ${COMPONENTS.tableHeader}px;
  background-color: ${LAYER_COLORS.two};

  span {
    width: fit-content;
  }
`;

export const MidRow = styled(MiddleRow)<{
  isPositiveChange?: boolean;
}>`
  /* 11px (DESIGN xs), not 12px: this row carries three separate metrics inside
     a 280px orderbook panel. */
  ${FONT_CSS.data.four}
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Without a gap the three metrics butt straight into each other — "…$1,650.00Mid:…". */
  gap: ${SPACING.two}px;
  padding: 0 ${SPACING.two}px;
  height: ${COMPONENTS.perpsOrderbookSpreadRowHeight}px;
  /* The row is a fixed 24px, so a wrapped price would be clipped mid-glyph.
     Keep every metric on one line and never paint outside the panel. */
  white-space: nowrap;
  overflow: hidden;
  color: ${({ isPositiveChange }) => {
    if (isPositiveChange !== undefined) {
      return isPositiveChange ? COLORS.up.one : COLORS.down.one;
    }
    return TEXT_COLORS.one;
  }};

  svg {
    ${({ isPositiveChange }) =>
      isPositiveChange !== undefined
        ? css`
            stroke: ${isPositiveChange
              ? COLORS.up.one
              : COLORS.down.one};
            transform: ${isPositiveChange ? "rotate(180deg)" : "rotate(0deg)"};
          `
        : css`
            display: none;
          `}
  }
`;

export const MarkPriceIV = styled.span`
  ${FONT_CSS.data.four}
  color: ${TEXT_COLORS.three};
  margin-left: ${SPACING.three}px;
`;

export const RowUpdater = styled.div`
  cursor: pointer;
  position: relative;
`;

export const PositionDetails = styled.div<{
  positionType: "ask" | "bid";
  indexFromLastRow: number;
}>`
  position: absolute;
  left: ${SPACING.two}px;
  border: 1px solid ${BORDER_COLORS.one};
  z-index: 100 !important;
  bottom: 0;
  border-radius: ${BORDER_RADIUS.five}px;
  height: fit-content;
  width: fit-content;

  ${({ positionType, indexFromLastRow }) => {
    if (positionType === "ask") {
      return `transform: translateY(max(45%, calc(95% - ${indexFromLastRow}*21px - 8px)));`;
    }
    return `transform: translateY(min(45%, calc(${indexFromLastRow}*21px - 8px)));`;
  }}

  > * {
    padding: ${SPACING.two}px;
  }

  ${frostedGlassBg(BACKGROUND_COLORS.nine, BACKGROUND_COLORS.seven, 8)}
`;

export const DetailHeader = styled.div`
  padding: ${SPACING.two}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  margin-bottom: ${SPACING.one}px;
  text-transform: capitalize;
`;

export const DetailBlock = styled.div`
  margin: ${SPACING.two}px auto;
  padding: 0 ${SPACING.two}px;
`;

export const TradeCol = styled.div<{ isHeader?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  padding: 0px ${SPACING.one}px !important;
  ${({ isHeader }) =>
    isHeader
      ? css`
          ${FONT_CSS.body.five}
          color: ${TEXT_COLORS.three};
        `
      : css`
          ${FONT_CSS.data.three}
        `}

  > span {
    justify-content: flex-end;
  }
`;

export const IVValue = styled.span`
  ${FONT_CSS.data.four}
  color: ${TEXT_COLORS.one};
  text-align: right;
`;

export const SpreadText = styled.span`
  ${FONT_CSS.data.three}
  color: ${TEXT_COLORS.three};
  font-weight: 400;
`;

/**
 * One labelled metric in the orderbook spread row (Last / Mid / Spread).
 *
 * Shrinks and ellipsises rather than overflowing: on a narrow panel or a
 * six-figure market (BTC at 104,235.50) three metrics cannot all fit, and the
 * previous markup simply painted past the panel edge — which is how the spread's
 * "%" ended up clipped off. Each metric carries a `title` with its full value,
 * so nothing is ever unreadable.
 */
export const MidMetric = styled.span`
  display: flex;
  align-items: center;
  gap: ${SPACING.one}px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Tertiary label ("Last"), never shrinks — a truncated label is unreadable. */
export const MidMetricLabel = styled.span`
  color: ${TEXT_COLORS.three};
  font-weight: 400;
  flex-shrink: 0;
`;

export const MidMetricValue = styled.span`
  color: ${TEXT_COLORS.one};
`;

export const BidAskBarWrapper = styled.div`
  display: flex;
  height: 28px;
  width: 100%;
  overflow: hidden;
  flex-shrink: 0;
`;

export const BidBar = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${SPACING.two}px;
  background-color: ${COLORS.up.three};
  color: ${COLORS.up.one};
  ${FONT_CSS.label.three}
  white-space: nowrap;
`;

export const AskBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 ${SPACING.two}px;
  flex: 1;
  background-color: ${COLORS.down.three};
  color: ${COLORS.down.one};
  ${FONT_CSS.label.three}
  white-space: nowrap;
`;

export const ProtectionBarrierRow = styled.div<{ $type: "bid" | "ask" }>`
  ${FONT_CSS.label.three}
  text-align: center;
  padding: ${SPACING.two}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};
  cursor: help;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $type }) => {
    if ($type === "bid") {
      return `
        color: ${COLORS.up.one};
        background-color: ${COLORS.up.six};

        > svg {
          stroke: ${COLORS.up.one};
          margin-left: ${SPACING.two}px;
        }
      `;
    }

    return `
      color: ${COLORS.down.one};
      background-color: ${COLORS.down.six};

      > svg {
        stroke: ${COLORS.down.one};
        margin-left: ${SPACING.two}px;
      }
    `;
  }}
`;
